import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export interface BreadcrumbItem {
  name: string;
  /** Absolute path for links. Omit on the last (current) item. */
  href?: string;
}

/**
 * Visible breadcrumb trail. Matches the BreadcrumbList JSON-LD emitted on the
 * same page so users and crawlers see the same hierarchy.
 *
 * The last item is rendered as plain text (aria-current="page") — it should
 * mirror the final ListItem in the JSON-LD payload.
 */
export const Breadcrumbs = ({ items }: { items: BreadcrumbItem[] }) => {
  if (!items.length) return null;
  return (
    <nav aria-label="Breadcrumb" className="mb-6">
      <ol className="flex flex-wrap items-center gap-1.5 text-[11px] tracking-[0.14em] uppercase text-muted-foreground">
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          return (
            <li key={`${item.name}-${i}`} className="flex items-center gap-1.5">
              {item.href && !isLast ? (
                <Link
                  to={item.href}
                  className="hover:text-primary transition-colors"
                >
                  {item.name}
                </Link>
              ) : (
                <span
                  aria-current={isLast ? "page" : undefined}
                  className={isLast ? "text-foreground/80" : ""}
                >
                  {item.name}
                </span>
              )}
              {!isLast && <ChevronRight className="w-3 h-3 opacity-60" aria-hidden />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
